import { z } from 'zod';

// 兩個 surface（admin / operator）共用的 InventoryTransformation Zod schema。
// 設計取捨：status / kind / reason_code / snapshot 都直接 mirror 後端 enum 與
// SnapshotBuilder 寫進去的固定結構，admin / operator 對這些 source-of-truth 沒有理由
// 分歧；list / detail 等 surface-specific shape 仍各自宣告。

// Mirror InventoryTransformation#status enum (Rails)。`processing` 是 ProcessService#approve
// 進 per-item loop 前的中間態，crash 後維持 :processing 給 ops 介入。
export const InventoryTransformationStatusSchema = z.enum([
  'pending',
  'processing',
  'applied',
  'partially_applied',
  'failed',
  'rejected',
]);
export type InventoryTransformationStatus = z.infer<typeof InventoryTransformationStatusSchema>;

// Mirror InventoryTransformationItem#status enum。
export const InventoryTransformationItemStatusSchema = z.enum([
  'pending',
  'applied',
  'failed',
]);
export type InventoryTransformationItemStatus = z.infer<typeof InventoryTransformationItemStatusSchema>;

// Mirror InventoryTransformation#kind enum。
export const InventoryTransformationKindSchema = z.enum([
  'attribute_change',
  'purpose_reallocation',
  'auto_reclassify',
]);
export type InventoryTransformationKind = z.infer<typeof InventoryTransformationKindSchema>;

// Mirror InventoryTransformationItem::REASON_CODES 常數。
export const ReasonCodeSchema = z.enum([
  'DAMAGED',
  'EXPIRED',
  'COUNT_ERROR',
  'DATA_ENTRY_ERROR',
  'QUALITY_CHECK_FAILED',
  'OTHER',
  'AUTO_EXPIRY_RECLASSIFY',
]);
export type ReasonCode = z.infer<typeof ReasonCodeSchema>;

// triggered_by_rule_snapshot 結構由 InventoryTypeReclassifyRules::SnapshotBuilder 寫進 DB，
// schema_version: 1（auto_reclassify_worker.rb 呼叫處），不是 ad-hoc payload。
// 抽出共用避免 admin / operator Show.tsx 各自 inline cast。
export const RuleSnapshotSchema = z.object({
  schema_version: z.number().int().optional(),
  rule: z.object({
    id: z.string().optional(),
    name: z.string(),
    priority: z.number(),
    source_inventory_type: z.object({ name: z.string() }).passthrough(),
    target_inventory_type: z.object({ name: z.string() }).passthrough(),
  }).passthrough(),
  matched_tier: z.object({
    id: z.string().optional(),
    original_expiration_date_threshold: z.number(),
    trigger_mechanism: z.enum([ 'by_days', 'by_ratio' ]),
    days_before_expiration: z.number().nullable(),
    expiration_ratio: z.string().nullable(),
  }).passthrough(),
  evaluated_at: z.string(),
}).passthrough();
export type RuleSnapshot = z.infer<typeof RuleSnapshotSchema>;

// failure_interpolations 限定為 primitive，避免 react-i18next 對 nested object silent fail。
// Zod 4: z.record 必須 explicit key type — z.record(z.string(), valueType)，不可 z.record(valueType)。
export const FailureInterpolationsSchema = z.record(
  z.string(),
  z.union([ z.string(), z.number(), z.boolean() ])
).nullable();
